import { useCallback, useEffect, useState } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useUser } from './useUser';
import type { RolApp } from './useSession';

export interface CurrentUserProfile {
  id: string;
  email: string;
  nombre: string;
  /** Override de display name. NULL = usar `nombre`. */
  nickname: string | null;
  rol_app: RolApp;
}

interface UseCurrentUserProfileResult {
  profile: CurrentUserProfile | null;
  loading: boolean;
  error: Error | null;
  /** Re-lee de DB. Usado tras editar nickname desde UserMenuDropdown. */
  refresh: () => Promise<void>;
}

/**
 * Lee la fila del usuario actual en `public.usuarios`. Selecciona solo los
 * campos visibles en el topbar/UserMenu (no PII de otros). RLS ya restringe
 * a `auth.uid()`.
 *
 * Falla blandita: si la fila aún no existe (caso borde — primer login post
 * temp password, bootstrap aún no corrió), devuelve profile=null y loading
 * sigue true hasta que el AuthGate dispare el bootstrap.
 */
export function useCurrentUserProfile(): UseCurrentUserProfileResult {
  const { user } = useUser();
  const [profile, setProfile] = useState<CurrentUserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!user?.id || !isSupabaseConfigured()) {
      setProfile(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const sb = getSupabase();
      const { data, error: qErr } = await sb
        .from('usuarios')
        .select('id, email, nombre, nickname, rol_app')
        .eq('id', user.id)
        .maybeSingle();
      if (qErr) throw qErr;
      setProfile((data ?? null) as CurrentUserProfile | null);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  return { profile, loading, error, refresh: load };
}
